"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DropdownBase from "../dropdown-base/dropdown-base.js";
import Input from "../input/index.js";
import List, { ListGroup, ListItem } from "../list/index.js";
import useCombinedRefs from "../utils/use-combined-refs.js";
import useDebounced from "../utils/use-debounced.js";
import useKeyboardNavigation from "../utils/use-keyboard-navigation.js";
const AUTOCOMPLETE_TIMEOUT = 500;
const defaultWrapper = ({ children }) => _jsx(_Fragment, { children: children });
function DefaultList({ attachmentWrapper: Wrapper = defaultWrapper, error, itemProps, loading, openRef, suggestions, value, onClick, onMouseEnter, onMouseLeave, }) {
    return (_jsx(Wrapper, { error: error, loading: loading, openRef: openRef, suggestions: suggestions || [], value: value, children: _jsx(List, { hoverable: true, overflow: false, children: (suggestions || []).map((sgroup, index) => {
                const items = sgroup.items.map(item => (_jsx(ListItem, Object.assign({ "data-item-id": item.id }, item.props, itemProps === null || itemProps === void 0 ? void 0 : itemProps(item), { onClick: e => onClick === null || onClick === void 0 ? void 0 : onClick(item, e), onMouseEnter: e => onMouseEnter === null || onMouseEnter === void 0 ? void 0 : onMouseEnter(item, e), onMouseLeave: e => onMouseLeave === null || onMouseLeave === void 0 ? void 0 : onMouseLeave(item, e), children: item.value }), item.id)));
                return sgroup.label ? (_jsx(ListGroup, { label: sgroup.label, children: items }, index)) : (items);
            }) }) }));
}
export const Autocomplete = React.forwardRef(function Autocomplete(_a, ref) {
    var { attachmentWrapper: Wrapper = defaultWrapper, autocompleteTimeout = AUTOCOMPLETE_TIMEOUT, block, className, closeOnSelection = true, dropdown = true, input: InputComponent = Input, itemProps, list: ListComponent = DefaultList, minLength = 2, openRef, strategy = "anchor-size", value: inputValue, autocompleteFn, onChange, onClose, onOpen, onSelectSuggestion, onSuggestionError } = _a, props = __rest(_a, ["attachmentWrapper", "autocompleteTimeout", "block", "className", "closeOnSelection", "dropdown", "input", "itemProps", "list", "minLength", "openRef", "strategy", "value", "autocompleteFn", "onChange", "onClose", "onOpen", "onSelectSuggestion", "onSuggestionError"]);
    const [suggestions, loading, error, executor] = useDebounced(autocompleteFn, autocompleteTimeout, "");
    const [value, setValue] = useState("");
    const innerOpenRef = useRef(null);
    const innerRef = useRef(null);
    const combinedRef = useCombinedRefs(ref, innerRef);
    useEffect(() => {
        if (openRef && openRef.current !== innerOpenRef.current) {
            openRef.current = innerOpenRef.current;
        }
    });
    useEffect(() => {
        const value = inputValue || "";
        setValue(value);
        executor(value.length < minLength ? "" : value);
    }, [executor, inputValue, minLength]);
    useEffect(() => {
        if (error) {
            onSuggestionError === null || onSuggestionError === void 0 ? void 0 : onSuggestionError(error);
        }
    }, [error, onSuggestionError]);
    const suggestionsMap = useMemo(() => Object.fromEntries((suggestions === null || suggestions === void 0 ? void 0 : suggestions.reduce((res, sgroup) => [...res, ...sgroup.items.map(item => [item.id, item])], [])) || []), [suggestions]);
    const labels = useMemo(() => Object.keys(suggestionsMap), [suggestionsMap]);
    const handleChange = useCallback((value, e) => {
        var _a, _b;
        onChange === null || onChange === void 0 ? void 0 : onChange(e.target.value, e);
        setValue(value);
        executor(value.length < minLength ? "" : value);
        if (!((_a = innerOpenRef.current) === null || _a === void 0 ? void 0 : _a.open))
            (_b = innerOpenRef.current) === null || _b === void 0 ? void 0 : _b.setOpen(true);
    }, [executor, minLength, onChange]);
    const triggerElement = _jsx(InputComponent, Object.assign({ block: block, value: value, onChange: handleChange }, props));
    const { current: preselection, handleKeyDown, setPreselection, } = useKeyboardNavigation(labels, (action, current, event) => {
        var _a, _b, _c;
        if (action === "close") {
            (_a = innerOpenRef === null || innerOpenRef === void 0 ? void 0 : innerOpenRef.current) === null || _a === void 0 ? void 0 : _a.setOpen(false);
        }
        else if (action === "toggle") {
            if (dropdown && !((_b = innerOpenRef === null || innerOpenRef === void 0 ? void 0 : innerOpenRef.current) === null || _b === void 0 ? void 0 : _b.open)) {
                (_c = innerOpenRef === null || innerOpenRef === void 0 ? void 0 : innerOpenRef.current) === null || _c === void 0 ? void 0 : _c.setOpen(true);
            }
            else if (current) {
                handleAct(suggestionsMap[current], event);
            }
        }
    }, false);
    const handleAct = useCallback((item, _e) => {
        var _a, _b;
        onSelectSuggestion === null || onSelectSuggestion === void 0 ? void 0 : onSelectSuggestion(item);
        if (closeOnSelection) {
            (_a = innerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            (_b = innerOpenRef.current) === null || _b === void 0 ? void 0 : _b.setOpen(false);
            if (inputValue !== value) {
                setValue(inputValue || "");
                executor("");
            }
        }
    }, [closeOnSelection, executor, inputValue, onSelectSuggestion, value]);
    const handleMouseAction = useCallback((eventType) => (item) => {
        setPreselection(eventType === "onmouseenter" ? item.id : undefined);
    }, [setPreselection]);
    const wrapped = (_jsx(ListComponent, { attachmentWrapper: Wrapper, error: error, itemProps: itemProps, loading: loading, openRef: openRef, preselection: preselection, suggestions: suggestions, value: value, onClick: handleAct, onMouseEnter: handleMouseAction("onmouseenter"), onMouseLeave: handleMouseAction("onmouseleave") }));
    return dropdown ? (_jsx(DropdownBase, { ref: combinedRef, block: block, className: className, "data-component": "autocomplete", openRef: innerOpenRef, strategy: strategy, triggerElement: triggerElement, triggerStrategy: "open", onClose: onClose, onKeyDown: handleKeyDown, onOpen: onOpen, children: wrapped })) : (_jsxs("div", { className: className, onKeyDown: handleKeyDown, children: [triggerElement, wrapped] }));
});
export default Autocomplete;
//# sourceMappingURL=autocomplete.js.map